/**
 * iter87.199.61 — First-run wizard template picker.
 *
 * Operator ratified on 2026-02-16: replace the shared TemplatePicker
 * (sport tiles + nested dropdowns) inside the first-run wizard with a
 * flat scrollable list of tap-and-go template presets. Rationale:
 *
 *   - First-run admin doesn't know what they want yet — they want the
 *     shortest path from "we're doing this" to "we're playing"
 *   - Season/boost/pool config default sensibly (Full · Boost ON ·
 *     per-preset defaults) — admin edits later from Tournament Settings
 *   - Everything visible here respects the SAME Site-Admin visibility
 *     map that TemplatePicker reads, so hidden/coming_soon templates
 *     drop out automatically. Operator curates what's live per season
 *
 * The shared TemplatePicker stays untouched — it continues to power
 * pages/TournamentSetup.jsx and admin routes. The two pickers are
 * intentionally NOT sharing code (no shared refactor risk, cleanest
 * separation).
 *
 * Emitted state contract:
 *   - `template` — the SAME backend template key TemplatePicker emits
 *     (may be `pickem:*` or `dyn:*` prefixed for legacy pickem/dynamic
 *     bindings). Wizard's existing prefix parser continues to work.
 *   - `extras`  — merged into the createTournament payload verbatim.
 *     Handles `golf_binding_id` (per-golf-event presets) without
 *     leaning on picker-internal state the wizard couldn't see.
 */
import React from "react";
import { Check, Sparkles } from "lucide-react";
import { CLUB_LEAGUES, CLUB_LEAGUES_HALVES } from "./constants";
import { SPORT_META, SportIconTile, SportHeaderIcon } from "./SportIcon";

/**
 * Build the ordered preset list for the current visibility + bindings
 * snapshot. Pure function so the wizard can also import this to derive
 * the suggested tournament name from the picked preset.
 */
export function buildPresets({ clubBindings, golfBindings, visibilityOverrides, disabledTemplates }) {
    const vis = visibilityOverrides || {};
    const disabled = new Set(disabledTemplates || []);
    const stateFor = (key, dflt = "live") => vis[key] || dflt;
    const isLive = (key) => stateFor(key) === "live" && !disabled.has(key);

    const rows = [];

    // Each preset carries a richer shape (mock-style):
    //   - `label`   → bold title (e.g. "CFB Weekly — SEC")
    //   - `format`  → uppercase colored tag (e.g. "WEEKLY PICK'EM")
    //   - `sub`     → muted descriptor (e.g. "18 weeks · spread + straight-up")
    //   - `sport`   → drives grouping + icon + accent (see ./SportIcon)
    //                 (`football` | `soccer` | `golf`)

    // ---- NFL (top of the list — most crews start here) --------------
    rows.push(
        { id: "nfl_weekly", label: "Weekly Pick'em — NFL",     format: "Weekly Pick'em",      sub: "18 weeks · spread + straight-up", sport: "football", visKey: "nfl_boost_api",  template: "nfl_boost_api",         extras: {} },
        { id: "nfl_pot",    label: "Season Pot Pick'em — NFL", format: "Season Pot Pick'em",  sub: "Pick 8 teams from 4 pots",        sport: "football", visKey: "nfl_season_api", template: "pickem:nfl_season_api", extras: {} },
    );

    // ---- Soccer Weekly Spread (EPL/La Liga) — mirrors the NFL Weekly
    //      Pick'em card: boost picks ARE the whole game, full season only.
    //      iter87.199.165 — was missing entirely from this first-run
    //      picker (only ever wired into the full TournamentSetup wizard).
    //      IDs are prefixed to match shorthandFor()'s existing
    //      startsWith("premier")/startsWith("la_liga") checks.
    rows.push(
        { id: "premier_weekly", label: "Weekly Pick'em — EPL",      format: "Weekly Pick'em", sub: "Spread picks · full season", sport: "soccer", visKey: "epl_boost_api",    template: "epl_boost_api",    extras: {} },
        { id: "la_liga_weekly", label: "Weekly Pick'em — La Liga",  format: "Weekly Pick'em", sub: "Spread picks · full season", sport: "soccer", visKey: "la_liga_boost_api", template: "la_liga_boost_api", extras: {} },
    );

    // ---- Club Seasons (soccer) — one Pot Pick'em card per BUILTIN
    //      league (Premier League, La Liga, Champions League), gated
    //      by that league's OWN visibility key. iter87.199.181 — this
    //      used to only appear when an Event Binding existed for the
    //      league (conflating "data is cached" with "exposed to
    //      users" — a new binding, like UCL's, doesn't automatically
    //      mean the template is ready for end users). Draft mode isn't
    //      generated here — it's been globally superseded by Pot
    //      Pick'em for every club league (see TemplatePicker.jsx's
    //      `_clubModesFor`, iter87.199.37).
    for (const league of CLUB_LEAGUES) {
        rows.push({
            id:       `${league.key}_pot`,
            label:    `Season Pot Picks — ${league.label}`,
            format:   "Season Pot Picks",
            sub:      league.rounds < 38 ? `League phase · ${league.rounds} matchdays, no draft` : `${league.rounds} matchweeks · no draft`,
            sport:    "soccer",
            visKey:   league.key,
            template: `pickem:${league.key}`,
            extras:   {},
        });
    }

    // ---- Premier League / La Liga 1st-Half / 2nd-Half tiles
    //      (iter87.233 operator cleanup) — these two leagues no longer
    //      offer a "full season" preset at all (mid-season pickup), so
    //      each gets its own explicit Half card here — no dropdown,
    //      mirrors the CLUB_LEAGUES loop above. iter87.234.5 — each half
    //      now gated on its OWN visibility key (half.key, e.g.
    //      "premier_league_api__1h") instead of the shared underlying
    //      league key, so Product Ops can expose one half without the
    //      other. Matches TemplatePicker.jsx's clubHalfLeagues.
    for (const half of CLUB_LEAGUES_HALVES) {
        const halfRounds = Math.ceil(half.rounds / 2);
        rows.push({
            id:       `${half.key}_pot`,
            label:    `Season Pot Picks — ${half.label}`,
            format:   "Season Pot Picks",
            sub:      half.window === "second_half"
                ? `Matchweeks ${halfRounds + 1}–${half.rounds} · no draft`
                : `Matchweeks 1–${halfRounds} · no draft`,
            sport:    "soccer",
            visKey:   half.key,
            template: `pickem:${half.template}`,
            extras:   { season_window: half.window },
        });
    }

    // ---- Dynamic (uncurated) club leagues — leagues added via Event
    //      Bindings that have no hardcoded CLUB_LEAGUES entry yet.
    //      These genuinely still need a binding, since there's no
    //      template row to gate on until one gets added above.
    const dynBindings = (clubBindings || [])
        .filter((b) => b.league_id && !CLUB_LEAGUES.some((l) => l.key === b.event_id));
    for (const b of dynBindings) {
        rows.push({
            id:       `dyn_${b.id}`,
            label:    `Season Draft — ${b.label || "Club Season"}`,
            format:   "Club Season · Draft",
            sub:      "Season-long · win / draw / loss",
            sport:    "soccer",
            visKey:   "club_season_api",
            template: `dyn:${b.id}`,
            extras:   {},
        });
    }

    // ---- Cup Tournaments (soccer)
    rows.push(
        { id: "world_cup",    label: "Group + Knockout — World Cup", format: "Group + Knockout", sub: "Draft + bracket picks",       sport: "soccer", visKey: "world_cup_api",    template: "world_cup_api",    extras: {} },
        { id: "world_cup_ko", label: "Knockout Only — World Cup",    format: "Knockout Only",     sub: "Bracket picks · elimination", sport: "soccer", visKey: "world_cup_ko_api", template: "world_cup_ko_api", extras: {} },
    );

    // ---- Golf — one card per live binding × format.
    for (const b of (golfBindings || []).filter((x) => x?.id && x?.label)) {
        rows.push(
            { id: `golf_${b.id}_avg`,   label: `Lowest Average Score — ${b.label}`, format: "Lowest Average Score",  sub: "Pick 6 · lowest avg wins",  sport: "golf", visKey: "golf_pickem_lowest_avg_api", template: "golf_pickem_lowest_avg_api", extras: { golf_binding_id: b.id } },
            { id: `golf_${b.id}_draft`, label: `Position Draft — ${b.label}`,        format: "Position Draft",        sub: "Auto-snake · top 30 OWGR", sport: "golf", visKey: "golf_position_draft_api",    template: "golf_position_draft_api",    extras: { golf_binding_id: b.id } },
        );
    }

    // ---- MLB Playoffs (baseball) — one card, no sub-picker. Team Pick
    //      Pot + Champions Pick + Series Predictor, auto-materialized
    //      from Highlightly the moment the tournament is created.
    rows.push({
        id: "mlb_playoffs", label: "MLB Playoffs", format: "Pick Pot + Champions Pick",
        sub: "6-team pick pot · Series Predictor", sport: "baseball",
        visKey: "mlb_playoffs_api", template: "mlb_playoffs_api", extras: {},
    });

    // ---- NBA Playoffs (basketball) — sibling of MLB above, same
    //      Highlightly/RapidAPI vendor + card shape.
    rows.push({
        id: "nba_playoffs", label: "NBA Playoffs", format: "Pick Pot + Champions Pick",
        sub: "8-team pick pot · Series Predictor", sport: "basketball",
        visKey: "nba_playoffs_api", template: "nba_playoffs_api", extras: {},
    });

    // ---- NFL/CFB Playoffs (football) — iter87.230. Sibling shape to
    //      MLB/NBA above (Highlightly-backed pick pot), but no
    //      Champions Pick / Series Predictor — round-scored bracket
    //      (6→8→10→12) + Weekly Spread Boost between rounds instead.
    rows.push({
        id: "nfl_playoffs", label: "NFL Playoffs", format: "Pick Pot + Weekly Spread",
        sub: "14-team bracket · round-scored", sport: "football",
        visKey: "nfl_playoffs_api", template: "nfl_playoffs_api", extras: {},
    });
    rows.push({
        id: "cfb_playoffs", label: "CFB Playoffs", format: "Pick Pot + Weekly Spread",
        sub: "12-team bracket · round-scored", sport: "football",
        visKey: "cfb_playoffs_api", template: "cfb_playoffs_api", extras: {},
    });

    // ---- NWSL Playoffs (soccer) — iter87.361. Pure pick'em, NO Team
    //      Pick Pot at all (unlike MLB/NBA/NFL/CFB above) — Champions
    //      Pick + Dark Horse Pick + Match Predictor only, API-Football
    //      vendor (league=254).
    rows.push({
        id: "nwsl_playoffs", label: "NWSL Playoffs", format: "Champions + Dark Horse + Predictor",
        sub: "8-team bracket · pure pick'em", sport: "soccer",
        visKey: "nwsl_playoffs_api", template: "nwsl_playoffs_api", extras: {},
    });

    // ---- MLS Playoffs (soccer) — iter87.465.20. Pure pick'em, with a
    //      Wild Card Play-In Pick (new primitive, pre-season) +
    //      Champions/Dark Horse (pre-season) + Round One Series
    //      Predictor + reused Playoff Predictor (in-season), API-
    //      Football vendor (league=253).
    rows.push({
        id: "mls_playoffs", label: "MLS Playoffs", format: "Champions + Dark Horse + Play-In",
        sub: "18-team bracket · pure pick'em", sport: "soccer",
        visKey: "mls_playoffs_api", template: "mls_playoffs_api", extras: {},
    });

    // iter87.466.1 — UCL Playoffs: Champion/Dark Horse (pre-season) +
    // Advance Predictor (every 2-leg round) + Champions Predictor
    // (Final only), API-Football vendor (league=2).
    rows.push({
        id: "ucl_playoffs", label: "UCL Playoffs", format: "Champion + Dark Horse + Two-Leg Predictor",
        sub: "24-team field · pure pick'em", sport: "soccer",
        visKey: "ucl_playoffs_api", template: "ucl_playoffs_api", extras: {},
    });

    // Filter out anything the Site-Admin has hidden or marked
    // coming_soon. Preview shows everything; production curated.
    return rows.filter((r) => isLive(r.visKey));
}


/**
 * Derive an "Our — X" tournament name from a picked preset. The
 * wizard imports this so the auto-name behaviour stays consistent
 * across every preset (no fall-through to stale TEMPLATES lookup).
 */
export function suggestedNameFor(preset, leagueName) {
    if (!preset) return "";
    return `${leagueName || "Our"} — ${preset.label}`;
}


// Static accent maps — Tailwind JIT needs literal class names.
const ACCENT = {
    cyber: { text: "text-cyber", ring: "border-cyber",   glow: "shadow-[0_0_28px_-10px_rgba(255,204,0,0.6)]",   bg: "bg-cyber/[0.06]" },
    neon:  { text: "text-neon",  ring: "border-neon",    glow: "shadow-[0_0_28px_-10px_rgba(57,255,20,0.55)]",  bg: "bg-neon/[0.06]"  },
    volt:  { text: "text-volt",  ring: "border-volt",    glow: "shadow-[0_0_28px_-10px_rgba(0,122,255,0.6)]",   bg: "bg-volt/[0.06]"  },
};
const DEFAULT_ACCENT = ACCENT.cyber;

// iter87.199.180 — Sport-grouped layout (user-requested redesign,
// 2026-08-31). Cards are grouped under a section header per sport
// instead of one flat list; the sport itself now carries the accent
// color (was per-card before), and icons are monochrome duotone
// (see ./SportIcon) instead of colored emoji.
const SECTION_ORDER = ["football", "soccer", "golf", "baseball", "basketball"];

// How many cards show per section before the global "Show more
// formats" reveal (design proposal Option A — global button, top 2
// cards/section visible up front).
const VISIBLE_PER_SECTION = 2;


export default function FirstRunTemplatePicker({
    selectedPresetId,
    visibilityOverrides,
    disabledTemplates,
    clubBindings,
    golfBindings,
    onPick,
}) {
    const presets = React.useMemo(
        () => buildPresets({ clubBindings, golfBindings, visibilityOverrides, disabledTemplates }),
        [clubBindings, golfBindings, visibilityOverrides, disabledTemplates],
    );

    const sections = React.useMemo(() => {
        const bySport = {};
        for (const p of presets) (bySport[p.sport] ||= []).push(p);
        return SECTION_ORDER
            .map((sport) => ({ sport, presets: bySport[sport] || [] }))
            .filter((s) => s.presets.length > 0);
    }, [presets]);

    // Global expand — reveals the tail of EVERY section at once.
    // iter87.451 (user-requested — "show without an extra click") —
    // defaults to expanded now, so every live format is visible on
    // first render; the collapse/"N more formats" toggle stays wired
    // for anyone who later wants it back, just unused by default.
    const [expanded, setExpanded] = React.useState(true);
    const hiddenCount = sections.reduce(
        (sum, s) => sum + Math.max(0, s.presets.length - VISIBLE_PER_SECTION), 0,
    );
    const selectedIsHidden = sections.some((s) => {
        const idx = s.presets.findIndex((p) => p.id === selectedPresetId);
        return idx >= VISIBLE_PER_SECTION;
    });
    const shouldExpand = expanded || selectedIsHidden || hiddenCount === 0;

    return (
        <div className="space-y-5" data-testid="first-run-template-picker">
            {presets.length === 0 && (
                <div
                    data-testid="first-run-picker-empty"
                    className="rounded-xl border border-white/10 bg-[#0e0e0e] p-4 text-sm text-zinc-400 leading-relaxed"
                >
                    Nothing is live to pick right now — every template is
                    currently hidden by the Site-Admin visibility registry.
                    Use the full setup wizard below if you need to build
                    something custom.
                </div>
            )}

            {sections.map((section, sIdx) => {
                const meta = SPORT_META[section.sport];
                const visible = shouldExpand ? section.presets : section.presets.slice(0, VISIBLE_PER_SECTION);
                return (
                    <div key={section.sport} className={sIdx > 0 ? "pt-4 border-t border-white/[0.06]" : ""}>
                        <div
                            data-testid={`first-run-section-${section.sport}`}
                            className={`flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.2em] mb-2.5 ${ACCENT[meta.accent]?.text || "text-zinc-500"}`}
                        >
                            <SportHeaderIcon sport={section.sport} />
                            {meta.label}
                        </div>
                        <div className="space-y-2.5">
                            {visible.map((p) => {
                                const selected = selectedPresetId === p.id;
                                const accent = ACCENT[meta.accent] || DEFAULT_ACCENT;
                                return (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => onPick?.(p)}
                                        data-testid={`first-run-preset-${p.id}`}
                                        data-selected={selected ? "1" : "0"}
                                        className={`w-full text-left rounded-2xl border-2 transition-all active:scale-[0.99] flex items-center gap-3.5 px-4 py-4 ${
                                            selected
                                                ? `${accent.ring} ${accent.bg} ${accent.glow}`
                                                : "border-white/[0.06] bg-[#141414] hover:border-white/[0.14]"
                                        }`}
                                    >
                                        <SportIconTile sport={section.sport} />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-base font-black text-white leading-tight">
                                                {p.label}
                                            </div>
                                            {p.format && (
                                                <div className={`text-[10px] font-black uppercase tracking-widest mt-1 ${accent.text}`}>
                                                    {p.format}
                                                </div>
                                            )}
                                            <div className="text-[11px] text-zinc-500 mt-1 leading-snug">
                                                {p.sub}
                                            </div>
                                        </div>
                                        {selected && <Check className={`w-5 h-5 shrink-0 ${accent.text}`} strokeWidth={2.75} />}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );
            })}

            {/* Global "N more formats" reveal — hidden once everything
                is visible OR the current selection forced an auto-expand. */}
            {!shouldExpand && hiddenCount > 0 && (
                <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    data-testid="first-run-show-more-templates"
                    className="w-full text-center text-[10px] font-black uppercase tracking-widest text-zinc-500 hover:text-cyber py-4 transition-colors"
                >
                    {hiddenCount} more format{hiddenCount === 1 ? "" : "s"}
                </button>
            )}

            <div className="mt-2 flex items-start gap-2 text-[11px] text-zinc-500 leading-relaxed">
                <Sparkles className="w-3.5 h-3.5 text-cyber mt-0.5 shrink-0" />
                <span data-testid="first-run-defaults-hint">
                    Season length, boosts, and scoring live in Tournament Settings — edit anytime.
                </span>
            </div>
        </div>
    );
}
